import { observable, computed, action } from "mobx";
import { mapGetOrCreate } from "../utils/mapGetOrCreate";
import { toMap } from "../utils/toMap";
import { ISODateTimeString, NetworkRequestStatistics } from "../../State/EvidenceDB/DatabaseTypes";
import { urlToDomain, getRegisteredDomain } from "../utils/domains";
import { NetworkOriginStatisticsTree, StatisticsNodeForRequestsForDomainNode, NetworkStatisticsNodeForRequestsForRegisteredDomain, UrlRequestStatistics, SourcePageAndDestinationPageUrlRequestStatistics, keyForRecordingStatisticsForSourcePageAndDestinationPageUrl, RecordingStatisticsForSourcePageAndDestinationPageUrlKey } from "./NetworkEventUrl";
import { LicensePermissions } from "../../State/EvidenceDB/LicensePermissions";


export const updateOriginNetworkStatisticsTreeForRequest = action( (
	root: NetworkOriginStatisticsTree,
	{url, numberOfRequests, whenFirstObservedIso, whenLastObservedIso}: ({url: string} & NetworkRequestStatistics)
) => {
		const domain = urlToDomain(url);
		const registeredDomain = getRegisteredDomain(domain);
		if (domain == null || registeredDomain == null) return;
		const registeredDomainObserved = mapGetOrCreate(root, registeredDomain, () => ({
			registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests: 0, domains: observable.map<string, StatisticsNodeForRequestsForDomainNode>()
		} satisfies NetworkStatisticsNodeForRequestsForRegisteredDomain as NetworkStatisticsNodeForRequestsForRegisteredDomain));
		registeredDomainObserved.numberOfRequests += numberOfRequests;
		if (whenFirstObservedIso < registeredDomainObserved.whenFirstObservedIso) {
			registeredDomainObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > registeredDomainObserved.whenLastObservedIso) {
			registeredDomainObserved.whenLastObservedIso = whenLastObservedIso;
		}

		const domainObserved = mapGetOrCreate(registeredDomainObserved.domains, domain, () => ({
			domain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests: 0, urls: observable.map<string, UrlRequestStatistics>()
		} satisfies StatisticsNodeForRequestsForDomainNode as StatisticsNodeForRequestsForDomainNode));
		domainObserved.numberOfRequests += numberOfRequests;
		if (whenFirstObservedIso < domainObserved.whenFirstObservedIso) {
			domainObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > domainObserved.whenLastObservedIso) {
			domainObserved.whenLastObservedIso = whenLastObservedIso;
		}

		const urlObserved = mapGetOrCreate(domainObserved.urls, url, () => ({
			url, whenFirstObservedIso, whenLastObservedIso, numberOfRequests
		} satisfies UrlRequestStatistics as UrlRequestStatistics));
		if (whenFirstObservedIso < urlObserved.whenFirstObservedIso) {
			urlObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > urlObserved.whenLastObservedIso) {
			urlObserved.whenLastObservedIso = whenLastObservedIso;
		}
	}
)

export const recordingStatisticsForSourceAndDestinationUrlToTrees = (stats: Iterable<SourcePageAndDestinationPageUrlRequestStatistics>) => {
	const sourcePageOriginStatisticsTree = new Map<string, NetworkStatisticsNodeForRequestsForRegisteredDomain>();
	const destinationOriginStatisticsTree = new Map<string, NetworkStatisticsNodeForRequestsForRegisteredDomain>();

	for (const sourceAndDestinationUrlStats of stats) {
		// const key = keyForRecordingStatisticsForSourcePageAndDestinationPageUrl(sourceAndDestinationUrlStats);
		const {numberOfRequests, sourcePageUrl, destinationPageUrl, whenFirstObservedIso, whenLastObservedIso} = sourceAndDestinationUrlStats;
		const update = {whenFirstObservedIso, whenLastObservedIso, numberOfRequests};
		updateOriginNetworkStatisticsTreeForRequest(sourcePageOriginStatisticsTree, {
			url: sourcePageUrl, ...update
		});
		updateOriginNetworkStatisticsTreeForRequest(destinationOriginStatisticsTree, {
			url: destinationPageUrl, ...update
		});
	}

	return {sourcePageOriginStatisticsTree, destinationOriginStatisticsTree};
}


export const updateOriginNetworkStatisticsTree = action( (
	root: NetworkOriginStatisticsTree
) => (
	...urlCountPairs: ({url: string} & NetworkRequestStatistics)[]
): NetworkOriginStatisticsTree => {
	for (const urlCountPair of urlCountPairs) {
		updateOriginNetworkStatisticsTreeForRequest(root, urlCountPair)
	}
	return root;
} )

export const createOriginNetworkStatisticsTree = (urlCountPairs: ({url: string} & NetworkRequestStatistics)[]): NetworkOriginStatisticsTree => {
	return updateOriginNetworkStatisticsTree(observable.map<string, NetworkStatisticsNodeForRequestsForRegisteredDomain>())(...urlCountPairs);
}


export class OriginAndDestinationUrlsObserver {
	private _recordingStatisticsForSourcePageAndDestinationUrls = observable.map<RecordingStatisticsForSourcePageAndDestinationPageUrlKey, SourcePageAndDestinationPageUrlRequestStatistics>();
	private _sourcePageUrls = observable.set<string>();
	private _sourcePageDomains = observable.set<string>();
	private _sourcePageRegisteredDomains = observable.set<string>();
	private _sourcePageOriginStatisticsTree = observable.map<string, NetworkStatisticsNodeForRequestsForRegisteredDomain>();
	private _destinationOriginStatisticsTree = observable.map<string, NetworkStatisticsNodeForRequestsForRegisteredDomain>();

	_whenFirstObservedIso: ISODateTimeString | undefined;
	_whenLastObservedIso: ISODateTimeString | undefined;

	@computed get isEmpty() {
		return this._sourcePageOriginStatisticsTree.size === 0;
	}

	@computed get sourcePageUrls() {
		return [...this._sourcePageUrls].sort();
	}
	@computed get sourcePageDomains() {
		return [...this._sourcePageDomains].sort();
	}
	@computed get sourcePageRegisteredDomains() {
		return [...this._sourcePageRegisteredDomains].sort();
	}

	@computed get sourcePageAndDestinationPageStatistics() {
		return this._recordingStatisticsForSourcePageAndDestinationUrls;
	}
	@computed get sourcePageOriginStatisticsTree() {
		return this._sourcePageOriginStatisticsTree;
	}
	@computed get destinationOriginStatisticsTree() {
		return this._destinationOriginStatisticsTree;
	}

	@computed get whenFirstObservedIso() {
		return this._whenFirstObservedIso;
	}
	@computed get whenLastObservedIso() {
		return this._whenLastObservedIso;
	}

	@computed get sourceRegisteredDomainToDestinations() {
		return [...toMap(
			[...this.sourcePageAndDestinationPageStatistics.values()],
			({sourcePageUrl}) => getRegisteredDomain(sourcePageUrl))
			.entries()]
			.map( ([registeredDomain, pairs]) => [
				registeredDomain, pairs
				// remove first party domains
				// pairs.filter( (pair) => getRegisteredDomain(pair.destinationPageUrl) !== registeredDomain )
			] as const )
			.filter( (pair): pair is [string, SourcePageAndDestinationPageUrlRequestStatistics[]] =>
				pair[0] != null && pair[1].length > 0
			)
			.map( ([sourceRd, urlParis]) => ([
				sourceRd,
				createOriginNetworkStatisticsTree(urlParis.map( ({sourcePageUrl: _, destinationPageUrl, ...metadata}) => ({
					...metadata,
					url: destinationPageUrl
				}))
		)] as const))
	}

	@computed get sourceRegisteredDomainToThirdPartyDestinations() {
		return this.sourceRegisteredDomainToDestinations.map( ([sourceRegisteredDomain, tree]) =>
			([sourceRegisteredDomain, new Map([...tree.entries()].filter( ([destinationRegisteredDomain]) => destinationRegisteredDomain !== sourceRegisteredDomain ))]) as [string, NetworkOriginStatisticsTree]
		).filter( ([, tree]) => tree.size > 0 )
	}

	destinationNetworkOriginStatisticsTreeFilteredBySourcePage = (
			sourceUrlFilter: (url: string) => boolean,
			thirdPartiesOnly: boolean
		): NetworkOriginStatisticsTree => {
			let filteredPairs = [...this.sourcePageAndDestinationPageStatistics.values()];
			// apply the source URL filter
			filteredPairs = filteredPairs.filter( ({sourcePageUrl}) => sourceUrlFilter(sourcePageUrl) );
			if (thirdPartiesOnly) {
				// apply the third party filter
				filteredPairs = filteredPairs.filter( ({sourcePageUrl, destinationPageUrl}) =>
					getRegisteredDomain(sourcePageUrl) !== getRegisteredDomain(destinationPageUrl)
				);
			}
			const filteredUrls = filteredPairs.map( ({sourcePageUrl: _, destinationPageUrl, ...metadata}) => ({
				...metadata,
				url: destinationPageUrl
			}));
			return createOriginNetworkStatisticsTree(filteredUrls);
		}


	integrateChanges = action((updates: SourcePageAndDestinationPageUrlRequestStatistics[]) => {
		for (const update of updates) {
			const key = keyForRecordingStatisticsForSourcePageAndDestinationPageUrl(update);
			let {numberOfRequests} = update;
			const {sourcePageUrl, destinationPageUrl, whenFirstObservedIso, whenLastObservedIso} = update;
			if (sourcePageUrl.startsWith('http')) {
				const sourcePageDomain = urlToDomain(sourcePageUrl); 
				const sourcePageRegisteredDomain = getRegisteredDomain(sourcePageUrl);
				if (sourcePageDomain != null && sourcePageRegisteredDomain != null) {
					this._sourcePageUrls.add(sourcePageUrl);
					this._sourcePageDomains.add(sourcePageDomain);
					this._sourcePageRegisteredDomains.add(sourcePageRegisteredDomain);
				}
			}
			numberOfRequests -= this._recordingStatisticsForSourcePageAndDestinationUrls.get(key)?.numberOfRequests ?? 0;
			const change = {whenFirstObservedIso, whenLastObservedIso, numberOfRequests};
			this._recordingStatisticsForSourcePageAndDestinationUrls.set(key, update);
			if (whenFirstObservedIso <= (this._whenFirstObservedIso ?? whenFirstObservedIso)) {
				this._whenFirstObservedIso = whenFirstObservedIso;
			}
			if (whenLastObservedIso >= (this._whenLastObservedIso ?? whenLastObservedIso)) {
				this._whenLastObservedIso = whenLastObservedIso;
			}
			updateOriginNetworkStatisticsTreeForRequest(this._sourcePageOriginStatisticsTree, {
				url: sourcePageUrl, ...change
			});
			updateOriginNetworkStatisticsTreeForRequest(this._destinationOriginStatisticsTree, {
				url: destinationPageUrl, ...change
			});
		}
	});

	constructor(protected readonly licensePermissions: LicensePermissions, updates: SourcePageAndDestinationPageUrlRequestStatistics[] = []) {
		this.integrateChanges(updates);
	}

}

